import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi } from '../../api/client';
import { User, UserRole } from '../../types';

const roleLabels: Record<string, string> = {
  ADMIN: 'Administrator',
  MANAGER: 'Leder',
  DISPATCHER: 'Disponent',
  VIEWER: 'Læser',
};

const roleColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  ADMIN: 'error',
  MANAGER: 'primary',
  DISPATCHER: 'info',
  VIEWER: 'default',
};

export const UserManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'DISPATCHER' as UserRole,
  });

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await userApi.getAll();
      return res.data.data as User[];
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => userApi.create(data),
    onSuccess: async (res) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      handleCloseDialog();
      // Auto-send invitation email
      try {
        const userId = res.data.data.id;
        await userApi.sendSetupEmail(userId);
        setSnackbar({ open: true, message: `Bruger oprettet og invitation sendt til ${formData.email}`, severity: 'success' });
      } catch {
        setSnackbar({ open: true, message: 'Bruger oprettet, men invitation kunne ikke sendes. Brug "Gensend invitation" knappen.', severity: 'error' });
      }
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error?.response?.data?.error || 'Kunne ikke oprette bruger', severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<typeof formData> }) =>
      userApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      handleCloseDialog();
      setSnackbar({ open: true, message: 'Bruger opdateret', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error?.response?.data?.error || 'Kunne ikke opdatere bruger', severity: 'error' });
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      userApi.update(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const sendInviteMutation = useMutation({
    mutationFn: (userId: string) => userApi.sendSetupEmail(userId),
    onSuccess: () => {
      setSnackbar({ open: true, message: 'Invitation sendt', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error?.response?.data?.error || 'Kunne ikke sende invitation', severity: 'error' });
    },
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (edit: boolean = false) => {
    setIsEditing(edit);
    if (edit && selectedUser) {
      setFormData({
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName,
        email: selectedUser.email,
        phone: (selectedUser as any).phone || '',
        role: selectedUser.role,
      });
    } else {
      setFormData({ firstName: '', lastName: '', email: '', phone: '', role: 'DISPATCHER' });
    }
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setFormData({ firstName: '', lastName: '', email: '', phone: '', role: 'DISPATCHER' });
  };

  const handleSubmit = () => {
    if (isEditing && selectedUser) {
      updateMutation.mutate({ id: selectedUser.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleToggleStatus = () => {
    if (selectedUser) {
      toggleStatusMutation.mutate({ id: selectedUser.id, isActive: !selectedUser.isActive });
    }
    handleMenuClose();
  };

  const handleResendInvite = () => {
    if (selectedUser) {
      sendInviteMutation.mutate(selectedUser.id);
    }
    handleMenuClose();
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Brugerstyring
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Opret og administrer brugere i din organisation
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog(false)}
          sx={{ borderRadius: 1 }}
        >
          Tilføj bruger
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Kunne ikke hente brugere
        </Alert>
      )}

      <Card sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Navn</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rolle</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sidst logget ind</TableCell>
                <TableCell align="right">Handlinger</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users?.map((user: User) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Typography fontWeight={500}>
                      {user.firstName} {user.lastName}
                    </Typography>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={roleLabels[user.role] || user.role}
                      color={roleColors[user.role] || 'default'}
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={user.isActive ? 'Aktiv' : 'Inaktiv'}
                      color={user.isActive ? 'success' : 'default'}
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell>
                    {user.lastLoginAt
                      ? new Date(user.lastLoginAt).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
                      : <Typography variant="caption" color="text.secondary">Aldrig</Typography>
                    }
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      {!user.lastLoginAt && user.isActive && (
                        <Tooltip title="Send invitation">
                          <IconButton
                            size="small"
                            onClick={() => sendInviteMutation.mutate(user.id)}
                            disabled={sendInviteMutation.isPending}
                          >
                            <SendIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, user)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleOpenDialog(true)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Rediger
        </MenuItem>
        <MenuItem onClick={handleResendInvite}>
          <SendIcon fontSize="small" sx={{ mr: 1 }} />
          {selectedUser?.lastLoginAt ? 'Send nulstilling' : 'Gensend invitation'}
        </MenuItem>
        <MenuItem onClick={handleToggleStatus}>
          {selectedUser?.isActive ? (
            <>
              <BlockIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
              Deaktiver
            </>
          ) : (
            <>
              <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
              Aktiver
            </>
          )}
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Rediger bruger' : 'Tilføj bruger'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {!isEditing && (
              <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                Brugeren modtager en email med et link til at opsætte sin adgangskode.
              </Alert>
            )}
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                label="Fornavn"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                fullWidth
                required
              />
              <TextField
                size="small"
                label="Efternavn"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                fullWidth
                required
              />
            </Box>
            <TextField
              size="small"
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
              disabled={isEditing}
            />
            <TextField
              size="small"
              label="Telefon"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Rolle</InputLabel>
              <Select
                value={formData.role}
                label="Rolle"
                onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              >
                <MenuItem value="ADMIN">Administrator — fuld adgang</MenuItem>
                <MenuItem value="MANAGER">Leder — adgang til alle kunder</MenuItem>
                <MenuItem value="DISPATCHER">Disponent — adgang til tildelte kunder</MenuItem>
                <MenuItem value="VIEWER">Læser — kun visning</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuller</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              createMutation.isPending || updateMutation.isPending ||
              !formData.firstName || !formData.lastName || !formData.email
            }
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Gemmer...'
              : isEditing ? 'Gem' : 'Opret og send invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
